/**
 * Classification of external URLs for `shell.openExternal`-style handlers.
 *
 * We use a blocklist instead of an allowlist: the OS only dispatches URL
 * schemes that have a registered handler, so passing through
 * `obsidian://`, `vscode://`, etc. is safe in practice. Known-dangerous
 * schemes (XSS primitives and `file:` as an RCE vector on Windows) stay
 * explicitly blocked, with a per-scheme reason so blocked attempts produce a
 * useful error message instead of a generic "Invalid URL".
 */

export type UrlClassification =
  | { kind: 'dangerous'; scheme?: string; reason: string }
  | { kind: 'internal-deeplink' }
  | { kind: 'safe-external' }

/**
 * Blocked URL schemes (including trailing `:`) mapped to a human-readable
 * reason. The reason flows through to the toast users see when a blocked
 * URL gets clicked, so it should explain *why* not just *what*.
 */
const DANGEROUS_SCHEMES: ReadonlyMap<string, string> = new Map([
  ['javascript:', 'JavaScript URLs can execute arbitrary code in the renderer (XSS vector).'],
  ['data:', 'data: URLs can embed executable content and bypass scheme restrictions.'],
  ['vbscript:', 'VBScript URLs are a legacy script-execution vector.'],
  ['blob:', 'blob: URLs are renderer-scoped and do not resolve outside this window.'],
  [
    'file:',
    'file: URLs are blocked because shell.openExternal can launch local executables on Windows (Electron RCE class). Use an in-app preview block (html-preview, pdf-preview, image-preview, markdown-preview) or open the file from your OS file manager.',
  ],
])

/**
 * Our own deep-link schemes, matched as a family rather than one literal.
 *
 * The scheme is variant-dependent: the side-by-side dev build registers
 * `devartifactagents://` (apps/electron/src/main/app-variant.ts) and each
 * multi-instance dev run `artifactagents<n>://` (scripts/electron-dev.ts). While
 * this compared against the single literal `'artifactagents:'`, a dev-scheme URL
 * classified as `safe-external`, which is a security hole and not just a
 * cosmetic one: `internal-deeplink` is what makes a URL be handled *inside* the
 * app, under the `untrustedSource` gate that refuses actions coming from
 * AI/tool-generated chat content and browser-pane pages. Classified
 * `safe-external` instead, the same URL was handed to `shell.openExternal` →
 * the OS → straight back into this app's *trusted* protocol handler, which
 * dispatched the action. Both defences fail together, because the main-process
 * gate and the preload's execution-point check call this one function.
 *
 * A pattern rather than the exact scheme because this runs in three places that
 * cannot all learn the exact one: the main process knows it, but the preload
 * bundle is built without the `ARTIFACT_APP_VARIANT` define (see
 * apps/electron/package.json `build:preload`), and the WebUI/Viewer copy runs in
 * a browser with no environment at all — yet that copy decides whether an
 * anchor click reaches the OS protocol handler, so it needs the answer too.
 *
 * Over-matching is the safe direction and is why this is deliberately loose: a
 * URL wrongly called ours is merely refused external opening and then rejected
 * by the deep-link parser, whereas one wrongly called external re-enters through
 * the OS as trusted. The exact accepted set — what actually gets *executed* —
 * stays with apps/electron/src/main/deep-link-scheme.ts, which does know the
 * variant and is intentionally stricter than this.
 */
const INTERNAL_DEEPLINK_PROTOCOL_PATTERN = /^(?:dev)?artifactagents\d*:$/

/**
 * True for `artifactagents:` and any variant of it this app family registers.
 *
 * @param protocol a `URL.protocol` value, i.e. including the trailing `:`
 */
export function isInternalDeeplinkProtocol(protocol: string): boolean {
  return INTERNAL_DEEPLINK_PROTOCOL_PATTERN.test(protocol.toLowerCase())
}

export function classifyExternalUrl(rawUrl: string): UrlClassification {
  if (typeof rawUrl !== 'string' || rawUrl.trim() === '') {
    return { kind: 'dangerous', reason: 'URL is empty or whitespace-only.' }
  }

  let parsed: URL
  try {
    parsed = new URL(rawUrl.trim())
  } catch {
    return { kind: 'dangerous', reason: 'URL is malformed and cannot be parsed.' }
  }

  const protocol = parsed.protocol.toLowerCase()

  const blockedReason = DANGEROUS_SCHEMES.get(protocol)
  if (blockedReason) {
    return { kind: 'dangerous', scheme: protocol, reason: blockedReason }
  }

  if (isInternalDeeplinkProtocol(protocol)) {
    return { kind: 'internal-deeplink' }
  }

  return { kind: 'safe-external' }
}

export function isSafeExternalUrl(rawUrl: string): boolean {
  return classifyExternalUrl(rawUrl).kind === 'safe-external'
}

/**
 * Format a `dangerous` classification into a user-facing error message.
 * Returns an empty string for non-dangerous classifications.
 */
export function formatBlockedUrlError(classification: UrlClassification): string {
  if (classification.kind !== 'dangerous') return ''
  const suffix = classification.scheme ? ` (${classification.scheme})` : ''
  return `URL blocked${suffix}. ${classification.reason}`
}
